import { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-apollo';

import { getErrorString } from '../../../../../../util/normalize-errors';
import { sentryCaptureException } from '../../../../../../util/sentry';
import getGlobalAccountCardQuery from '../../../../../../graphql/queries/getGlobalAccountCard';
import { checkout as checkoutMutation } from '../../../../../../graphql/mutations/checkout';
import { checkoutPrice as checkoutPriceQuery } from '../../../../../../graphql/queries/checkout';

/**
 * Bulk Waldogram checkout hook.
 *
 * @param {Object} options The hook's params.
 * @param {Object} [options.accountCard] The AccountCard.
 * @param {function} [options.onSuccess] Success callback.
 * @returns {{onCheckout: (function(*): Promise<undefined>), checkingOut: boolean, checkoutSuccess: boolean, checkoutErrors: *[], loadingPricing: boolean, checkoutPrice: *}}
 */
const useGlobalWaldogramCheckout = ({ accountCard, onSuccess }) => {
  const [checkoutErrors, setCheckoutErrors] = useState([]);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const { data: { globalAccountCard } = {} } = useQuery(
    getGlobalAccountCardQuery,
  );
  const [checkout] = useMutation(checkoutMutation);

  const accountCardCheckoutInput = {
    custom_print_items: [
      {
        album_custom_print_id: globalAccountCard?.id,
        quantity: 1,
        configuration: {
          photos: [],
          account_card: {
            account_card_id: accountCard?.id,
          },
        },
      },
    ],
  };

  const onCheckout = async token => {
    if (checkingOut) {
      return;
    }

    setCheckoutErrors([]);
    setCheckingOut(true);

    try {
      await checkout({
        variables: {
          input: {
            stripe_token: token?.id,
            email_address: token?.email,
            stripe_payment_method_id: token?.stripe_payment_method_id,
            stripe_connect_account_id: token?.stripe_connect_account_id,
            platform: 'WEB',
            ...accountCardCheckoutInput,
          },
        },
      });

      onSuccess?.();
      setCheckoutSuccess(true);
    } catch (reponseError) {
      setCheckoutErrors([getErrorString(reponseError)]);

      sentryCaptureException(reponseError, accountCard);
    } finally {
      setCheckingOut(false);
    }
  };

  // Pricing
  //
  const {
    data: { checkoutPrice } = {},
    loading: loadingPricing,
    refetch: refetchPricing,
  } = useQuery(checkoutPriceQuery, {
    variables: {
      ...accountCardCheckoutInput,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !accountCardCheckoutInput || !accountCard || !globalAccountCard,
  });

  useEffect(() => {
    if (accountCard) {
      refetchPricing?.();
    }
  }, [accountCard]);

  return {
    checkoutErrors,
    checkingOut,
    checkoutSuccess,
    checkoutPrice,
    loadingPricing,
    onCheckout,
  };
};

export default useGlobalWaldogramCheckout;
